<template>
  <avs-card>
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedDepartment"
                clearable
                :items="departments"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Departements')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-sitemap"
              />
            </v-col>

            <!-- Actions (slot parent) -->
            <v-col cols="12" md="4" class="d-flex align-center justify-end">
              <slot name="actions" v-bind:dataset="dataset" />
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 px-4 pt-4">
        <v-text-field
          v-model="search"
          @input="onSearchChange"
          clearable
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          class="mb-3"
        />
        <v-autocomplete
          v-model="selectedDepartment"
          clearable
          :items="departments"
          @input="applyFilters"
          item-text="name"
          item-value="id"
          :label="$t('Departements')"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-sitemap"
          class="mb-2"
        />
      </div>
    </template>

    <!-- ===== TABLE ===== -->
    <template v-slot:content>
      <v-data-table
        class="ma-6 bgTable"
        :headers="headers"
        dense
        :items="dataset"
        :loading="loading"
        :no-data-text="$t('messages.information.no_data')"
        :no-results-text="$t('Aucune donnée disponible')"
        :loading-text="$t('Chargement des données')"
        disable-pagination
        hide-default-footer
        item-key="id"
      >
        <!-- Nom complet -->
        <template #item.full_name="{ item }">
          {{ fullName(item) }}
        </template>

        <template #item.monthly_limit="{ item }">
          {{ formatNumber(item.monthly_limit) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-editButton
              @click.stop="toEdit(item)"
              :spantext="$t('actions.edit', { entity: $tc('categories.cfsi.name', 1).toLowerCase() })"
            />
            <avs-deleteButton
              :disabled="isNCSigned(item)"
              @click="handleOpenDeleteDialog(item)"
              :spantext="$t('actions.delete', { entity: $tc('categories.cfsi.name', 1).toLowerCase() })"
            />
          </div>
        </template>
      </v-data-table>

      <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
        <v-progress-circular indeterminate color="primary" />
      </v-row>

      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Personnel"
      />
    </template>
  </avs-card>
</template>

<script>
import { debounce } from "lodash";
import StaffMembersService from "@/services/StaffMembersService";
import { DepartmentService } from "@/services";

export default {
  name: "StaffTable",
  data() {
    return {
      // pagination + state
      page: 1,
      dataset: [],
      loading: false,
      isFetchingMore: false,
      isLastPage: false,

      // filters
      search: "",
      selectedDepartment: null,
      departments: [],

      // ui / dialog
      getStaffDebounced: null,
      deleteDialogIsOpen: false,
      selectedItem: null,

      headers: [
        { text: this.$t("Nom & Prénom"), value: "full_name" },
        { text: this.$t("Departement"), value: "department_name" },
        { text: this.$tc("commons.email"), value: "email", align: "left" },
        { text: this.$t("N° Téléphone"), value: "phone_number" },
        { text: this.$t("Matricule"), value: "matricule", sortable: false },
        { text: this.$t("commons.actions"), value: "actions", align: "right" },
      ],
    };
  },
  computed: {
    company() { return this.$store.state.app.company; },
    department() { return this.$store.state.app.project; },
  },
  methods: {
    formatNumber(v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("fr-FR");
    },

    // >>> ICI : Nom + prénom ensemble
    fullName(item) {
      const first = item.first_name || "";
      const last = item.last_name || "";
      return `${last} ${first}`.trim() || "—";
    },

    getToEditLink(item) {
      return { name: "StaffCreateUpdate", params: { id: item.id } };
    },
    toEdit(item) { this.$router.push(this.getToEditLink(item)); },
    isNCSigned(item) { return item.state === 4; },

    getQueryParams() {
      return {
        search: this.search || undefined,
        department: this.selectedDepartment || undefined,
        page: this.page,
      };
    },

    async getStaff() {
      if (this.isFetchingMore || this.isLastPage) return;

      this.loading = this.page === 1;
      this.isFetchingMore = true;

      try {
        const response = await StaffMembersService.get(this, null, this.getQueryParams());
        const results = response?.results || [];

        if (!results.length) {
          this.isLastPage = true;
        } else {
          const seen = new Set(this.dataset.map(i => i.id));
          this.dataset.push(...results.filter(i => !seen.has(i.id)));
          this.page += 1;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          this.isLastPage = true;
        } else if (err?.response?.status === 403) {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.permission_denied"),
            color: "error",
          });
        }
      } finally {
        this.loading = false;
        this.isFetchingMore = false;
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
      }
    },

    onSearchChange() {
      this.getStaffDebounced();
    },

    applyFilters() {
      this.page = 1;
      this.dataset = [];
      this.isLastPage = false;
      this.getStaff();
    },

    handleScroll() {
      const scrollBottom = window.innerHeight + window.scrollY;
      const pageBottom = document.body.offsetHeight - 300;
      if (scrollBottom >= pageBottom) this.getStaff();
    },

    handleDelete() {
      StaffMembersService.remove(this, this.selectedItem.id).then(() => {
        this.dataset = this.dataset.filter(e => e.id !== this.selectedItem.id);
        // Recharger depuis le début pour garder la pagination cohérente
        this.page = 1;
        this.dataset = [];
        this.isLastPage = false;
        this.getStaff();

        this.$store.commit("app/message", {
          msg: this.$t("messages.success.deleted", { entity: this.$tc("categories.staff.name", 2) }),
          color: "success",
        });
      });
      this.deleteDialogIsOpen = false;
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true;
      this.selectedItem = item;
    },
    handleCancel() { this.deleteDialogIsOpen = false; },
  },

  async created() {
    // Charger les départements
    try {
      const companyId = this.company?.pk || this.company?.id;
      if (companyId) {
        const depts = await DepartmentService.getDepartment(companyId);
        this.departments = depts || [];
      }
    } catch (_) { /* noop */ }

    // Pré-sélection depuis le store si présent
    if (this.department && this.department.id) {
      this.selectedDepartment = this.department.id;
    }
  },

  mounted() {
    this.getStaffDebounced = debounce(() => {
      this.page = 1;
      this.dataset = [];
      this.isLastPage = false;
      this.getStaff();
    }, 400);

    this.getStaff();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
</style>
